import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import useTabInPath, { tabForSlug } from '../hooks/useTabInPath';
import { timesheetPath } from '../nav/paths';
import useTimesheetData from '../hooks/useTimesheetData';
import EntryCard from '../components/EntryCard';
import DateNavigator from '../components/DateNavigator';
import DailyTotal from '../components/DailyTotal';
import AddJobModal from '../components/AddJobModal';
import PeriodSummary from '../components/PeriodSummary';
import EntryListView from '../components/EntryListView';
import TimesheetSummary from '../components/TimesheetSummary';
import TabularSummary from '../components/TabularSummary';
import { useDebug } from '../contexts/DebugContext';
import { useAuth } from '../contexts/AuthContext';
// Calendar-date helpers: no browser time-zone logic (see utils/dates.js).
import { datesInRange, weekday, displayRange, toDateOnly } from '../utils/dates';
import { IconButton, Banner } from '../components/ui';

// Timesheet views and their addresses (/time/timesheets/:id = Tabular).
const TIMESHEET_VIEW_SLUGS = { entry: 'entry', bydate: 'by-date', byjob: 'by-job' };

export default function TimesheetDetailPage() {
  const { id, view } = useParams();
  const timesheetId = id;

  const { debugMode, setDebugMode } = useDebug();
  const { user } = useAuth();

  const {
    customers, rateCodes, favorites, entries, timesheet,
    loading, loadingStep, debugInfo, error, refreshEntries, updateSingleEntry, refreshFavorites, refreshTimesheet,
  } = useTimesheetData(timesheetId);

  const [currentDate, setCurrentDate] = useState(null);
  const [currentFavIndex, setCurrentFavIndex] = useState(0);
  const [showAddJob, setShowAddJob] = useState(false);
  const [activeTab, setActiveTab] = useState(() => tabForSlug(TIMESHEET_VIEW_SLUGS, view, 'tabular'));
  const [submitResult, setSubmitResult] = useState(null);

  const entryCardRef = useRef(null);

  // Build date range from timesheet period
  const dates = useMemo(() => {
    if (!timesheet) return [];
    return datesInRange(timesheet.PeriodStart, timesheet.PeriodEnd);
  }, [timesheet]);

  // Initialize current date to today (if within period) or first day of period.
  // "Today" comes from the web server (Central time), never the browser's clock.
  const serverToday = timesheet ? toDateOnly(timesheet.ServerToday) : '';
  useEffect(() => {
    if (dates.length > 0 && !currentDate) {
      setCurrentDate(dates.includes(serverToday) ? serverToday : dates[0]);
    }
  }, [dates, currentDate, serverToday]);

  // Current day's entry for the current favorite job/task
  const currentFav = favorites[currentFavIndex] || null;

  // Holiday rules (from the server): holiday jobs only on holiday dates, max hours.
  const holidays = (timesheet && timesheet.Holidays) || null;
  const currentIsHolidayJob = !!(holidays && currentFav && (holidays.jobs || []).includes(currentFav.JobID));
  const currentHolidayNA = currentIsHolidayJob && !Object.prototype.hasOwnProperty.call(holidays.dates || {}, currentDate);

  const currentEntry = useMemo(() => {
    if (!currentFav || !currentDate) return null;
    return entries.find(
      (e) =>
        e.EntryDate?.split('T')[0] === currentDate &&
        e.JobID === currentFav.JobID &&
        e.JobTaskID === currentFav.JobTaskID
    ) || null;
  }, [entries, currentFav, currentDate]);

  // Default rate code: use the rate from an existing entry for this job/task,
  // or fall back to the user's default work type from their Resource record
  const defaultRateCodeForFav = useMemo(() => {
    if (!currentFav) return null;
    // First: look for an entry matching this job/task
    const match = entries.find(
      (e) => e.JobID === currentFav.JobID && e.JobTaskID === currentFav.JobTaskID
    );
    if (match?.RateCodeID) return match.RateCodeID;
    // Fallback: use the user's default work type from their Resource record
    if (user?.defaultWorkType) return user.defaultWorkType;
    return null;
  }, [entries, currentFav, user?.defaultWorkType]);

  // Daily total (exclude 0-hour placeholder entries from display)
  const dailyTotal = useMemo(() => {
    if (!currentDate) return 0;
    return entries
      .filter((e) => e.EntryDate?.split('T')[0] === currentDate)
      .reduce((sum, e) => sum + (parseFloat(e.Hours) || 0), 0);
  }, [entries, currentDate]);

  // Check if any entry on the current day is missing a description (only for entries with hours > 0)
  const dailyMissingDescription = useMemo(() => {
    if (!currentDate) return false;
    const dayEntries = entries.filter((e) => e.EntryDate?.split('T')[0] === currentDate && parseFloat(e.Hours) > 0);
    return dayEntries.length > 0 && dayEntries.some((e) => !e.Description);
  }, [entries, currentDate]);

  // Check if any day exceeds 24 hours (for blocking submission)
  const hasOverLimitDay = useMemo(() => {
    const totals = {};
    for (const e of entries) {
      const d = e.EntryDate?.split('T')[0];
      totals[d] = (totals[d] || 0) + (parseFloat(e.Hours) || 0);
    }
    return Object.values(totals).some((t) => t > 24);
  }, [entries]);

  // Period total
  const periodTotal = useMemo(() => {
    return entries.reduce((sum, e) => sum + (parseFloat(e.Hours) || 0), 0);
  }, [entries]);

  // Expected hours: 8 per weekday (Mon-Fri) in the period
  const expectedHours = useMemo(() => {
    return dates.reduce((sum, d) => {
      const day = weekday(d);
      return sum + (day >= 1 && day <= 5 ? 8 : 0);
    }, 0);
  }, [dates]);

  // Helper: save current entry before navigating away
  const saveBeforeNav = useCallback(async () => {
    if (entryCardRef.current) {
      await entryCardRef.current.save();
    }
  }, []);

  // Navigation: left/right = prev/next day
  const dateIndex = dates.indexOf(currentDate);

  const goToPrevDay = useCallback(async () => {
    if (dateIndex > 0) {
      await saveBeforeNav();
      setCurrentDate(dates[dateIndex - 1]);
    }
  }, [dateIndex, dates, saveBeforeNav]);

  const goToNextDay = useCallback(async () => {
    if (dateIndex < dates.length - 1) {
      await saveBeforeNav();
      setCurrentDate(dates[dateIndex + 1]);
    }
  }, [dateIndex, dates, saveBeforeNav]);

  // Navigation: up/down = prev/next favorite
  const goToPrevFav = useCallback(async () => {
    if (currentFavIndex > 0) {
      await saveBeforeNav();
      setCurrentFavIndex(currentFavIndex - 1);
    }
  }, [currentFavIndex, saveBeforeNav]);

  const goToNextFav = useCallback(async () => {
    await saveBeforeNav();
    const readOnly = timesheet?.Status === 'Submitted' || timesheet?.Status === 'Approved';
    if (currentFavIndex < favorites.length - 1) {
      setCurrentFavIndex(currentFavIndex + 1);
    } else if (!readOnly) {
      setShowAddJob(true);
    }
  }, [currentFavIndex, favorites.length, saveBeforeNav, timesheet?.Status]);

  // Save entry (create or update)
  const handleSave = useCallback(async ({ hours, rateCodeId, rateSheetCode, description, nonBillable }) => {
    if (!currentFav) return;

    if (currentEntry) {
      const updated = await api.updateTimeEntry(currentEntry.TimeEntryID, {
        hours, rateCodeId, description, nonBillable,
      });
      updateSingleEntry(updated);
    } else {
      await api.createTimeEntry({
        timesheetId,
        jobId: currentFav.JobID,
        jobTaskId: currentFav.JobTaskID,
        entryDate: currentDate,
        hours,
        rateSheetCode: rateSheetCode || currentFav.RateSheetCode || '',
        rateCodeId,
        description,
        nonBillable,
      });
      await refreshEntries();
    }
  }, [currentFav, currentEntry, timesheetId, currentDate, refreshEntries, updateSingleEntry]);

  const handleDelete = useCallback(async () => {
    if (!currentEntry) return;
    await api.deleteTimeEntry(currentEntry.TimeEntryID);
    await refreshEntries();
  }, [currentEntry, refreshEntries]);

  const handleSubmit = async () => {
    if (hasOverLimitDay) {
      alert('Cannot submit: one or more days exceed 24 hours. Please correct before submitting.');
      return;
    }
    if (!window.confirm('Submit this timesheet? You will not be able to edit entries after submission.')) return;
    try {
      const result = await api.submitTimesheet(timesheetId);
      setSubmitResult(result);
      await refreshTimesheet();
    } catch (e) {
      setSubmitResult({ error: e.message });
      // Refresh to pick up any status change (e.g. Approved) from the server
      await refreshTimesheet();
    }
  };

  const handleReopen = async () => {
    try {
      await api.reopenTimesheet(timesheetId);
      setSubmitResult(null);
      await refreshTimesheet();
    } catch (e) {
      // Show error with NAV call debug info if available
      const result = { error: e.message || 'Failed to reopen timesheet' };
      if (e.responseBody?.navCall) {
        result.navCall = e.responseBody.navCall;
      }
      if (e.responseBody?._statusDebug) {
        result._statusDebug = e.responseBody._statusDebug;
      }
      setSubmitResult(result);
      // Refresh to pick up any status change (e.g. Approved) from the server
      await refreshTimesheet();
    }
  };

  const handleDeleteJob = async (jobId, jobTaskId) => {
    try {
      const pendingTimesheets = await api.getPendingHours(jobId, jobTaskId);
      const thisTs = pendingTimesheets.find(p => p.TimesheetID === timesheetId);
      const otherTs = pendingTimesheets.filter(p => p.TimesheetID !== timesheetId);

      if (thisTs && thisTs.TotalHours > 0) {
        alert(`This line cannot be deleted because there is ${thisTs.TotalHours.toFixed(2)} hours of pending time on this timesheet for this job/task. Remove the time entries first, then delete the line.`);
        return;
      }
      if (otherTs.length > 0) {
        const otherInfo = otherTs.map(t => `${displayRange(t.PeriodStart, t.PeriodEnd)} (${t.TotalHours.toFixed(2)} hrs)`).join('\n');
        alert(`This line cannot be deleted because there is pending time on another timesheet:\n\n${otherInfo}\n\nSubmit the other timesheet first or remove the time; then delete this line.`);
        return;
      }

      await api.deleteJobEntries(timesheetId, jobId, jobTaskId);
      const fav = favorites.find((f) => f.JobID === jobId && f.JobTaskID === jobTaskId);
      if (fav) {
        await api.removeFavorite(fav.UserFavoriteID);
        await refreshFavorites();
      }
      await refreshEntries();
      if (currentFavIndex >= favorites.length - 1) {
        setCurrentFavIndex(Math.max(0, favorites.length - 2));
      }
    } catch (e) {
      alert(e.message || 'Failed to delete job entries');
    }
  };

  const handleReorderJobs = useCallback(async (orderedFavoriteIds) => {
    try {
      await api.reorderFavorites(orderedFavoriteIds);
      await refreshFavorites();
    } catch (e) {
      console.error('Failed to reorder jobs:', e);
    }
  }, [refreshFavorites]);

  const handleMoveEntries = async (fromJobId, fromJobTaskId, toJobId, toJobTaskId, date, toDate) => {
    try {
      const payload = { timesheetId, fromJobId, fromJobTaskId, toJobId, toJobTaskId };
      if (date) payload.date = date;
      if (toDate) payload.toDate = toDate;
      await api.moveEntries(payload);
      await refreshEntries();
    } catch (e) {
      alert(e.message || 'Failed to move entries');
    }
  };

  const handleAddFavorite = async (jobId, jobTaskIds) => {
    // Accept a single task id or an array (multi-select). Individual time
    // entries are still assigned to a single job/task line.
    const ids = Array.isArray(jobTaskIds) ? jobTaskIds : [jobTaskIds];
    // Add each task line that isn't already a favorite.
    let addedAny = false;
    for (const tid of ids) {
      const exists = favorites.some((f) => f.JobID === jobId && f.JobTaskID === tid);
      if (!exists) {
        await api.addFavorite(jobId, tid);
        addedAny = true;
      }
    }
    const updatedFavs = await api.getFavorites();
    await refreshFavorites();
    setShowAddJob(false);
    // Navigate to the first task line requested (added or pre-existing).
    const target = ids[0];
    const newIdx = updatedFavs.findIndex(
      (f) => f.JobID === jobId && f.JobTaskID === target
    );
    if (newIdx >= 0) setCurrentFavIndex(newIdx);
    else if (addedAny) setCurrentFavIndex(updatedFavs.length - 1);
    setActiveTab('entry');
  };

  // Save before switching tabs
  const handleTabChange = async (tab) => {
    if (activeTab === 'entry') {
      await saveBeforeNav();
    }
    setActiveTab(tab);
  };
  useTabInPath({ base: timesheetPath(id), slugs: TIMESHEET_VIEW_SLUGS, tab: activeTab, defaultTab: 'tabular', onUrlTab: handleTabChange });

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner" />
        <div>Loading timesheet...</div>
        {loadingStep && (
          <div style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '4px' }}>
            {loadingStep}
          </div>
        )}
        {debugMode && debugInfo?.navHeaderCheck && (
          <div style={{
            marginTop: '8px', fontSize: '11px', color: 'var(--gray-500)', textAlign: 'left',
            maxWidth: '95vw', overflow: 'auto', fontFamily: 'monospace', wordBreak: 'break-all',
          }}>
            <div>GET {debugInfo.navHeaderCheck.url || debugInfo.navHeaderCheck.endpoint}</div>
            <div>HTTP {debugInfo.navHeaderCheck.httpStatus} | NAV Status: {debugInfo.navHeaderCheck.navStatus || 'N/A'} | {debugInfo.navHeaderCheck.durationMs}ms</div>
            {debugInfo.navHeaderCheck.error && <div style={{ color: '#dc2626' }}>Error: {debugInfo.navHeaderCheck.error}</div>}
            {debugInfo.navHeaderCheck.response && (
              <div style={{ marginTop: '2px' }}>Response: {JSON.stringify(debugInfo.navHeaderCheck.response)}</div>
            )}
          </div>
        )}
      </div>
    );
  }

  if (!timesheet) {
    return (
      <div className="empty-state">
        <p>Timesheet not found.</p>
        {error && <Banner kind="err">Error: {error}</Banner>}
      </div>
    );
  }

  const isSubmitted = timesheet.Status === 'Submitted';
  const isApproved = timesheet.Status === 'Approved';
  const isReadOnly = isSubmitted || isApproved;

  return (
    <>
      {/* Tab Bar */}
      <div className="tab-bar">
        <button className={activeTab === 'entry' ? 'active' : ''} onClick={() => handleTabChange('entry')}>Entry</button>
        <button className={activeTab === 'bydate' ? 'active' : ''} onClick={() => handleTabChange('bydate')}>By Date</button>
        <button className={activeTab === 'byjob' ? 'active' : ''} onClick={() => handleTabChange('byjob')}>By Job</button>
        <button className={activeTab === 'tabular' ? 'active' : ''} onClick={() => handleTabChange('tabular')}>Tabular</button>
      </div>



      {/* Period Summary */}
      <PeriodSummary
        timesheet={timesheet}
        periodTotal={periodTotal}
        expectedHours={expectedHours}
        entryCount={entries.filter((e) => parseFloat(e.Hours) > 0).length}
      />

      {activeTab === 'entry' ? (
        <>
          {/* Date Navigation with Daily Total inline */}
          <DateNavigator
            currentDate={currentDate}
            onPrev={goToPrevDay}
            onNext={goToNextDay}
            hasPrev={dateIndex > 0}
            hasNext={dateIndex < dates.length - 1}
            onDateSelect={async (d) => { if (dates.includes(d)) { await saveBeforeNav(); setCurrentDate(d); } }}
            minDate={dates[0]}
            maxDate={dates[dates.length - 1]}
            rightContent={<DailyTotal hours={dailyTotal} missingDescription={dailyMissingDescription} />}
          />

          {/* Entry Card with integrated job nav */}
          {favorites.length === 0 ? (
            <div className="empty-state">
              <p>No favorite jobs yet.{!isReadOnly && ' Add a job/task to start entering time.'}</p>
              {!isReadOnly && (
                <button className="btn btn-primary" onClick={() => setShowAddJob(true)}>
                  + Add Job / Task
                </button>
              )}
            </div>
          ) : (
            <>
              <EntryCard
                ref={entryCardRef}
                key={`${currentDate}-${currentFavIndex}`}
                favorite={currentFav}
                entry={currentEntry}
                rateCodes={rateCodes}
                defaultRateCodeId={defaultRateCodeForFav}
                isSubmitted={isReadOnly}
                holidayNA={currentHolidayNA}
                maxHours={currentIsHolidayJob ? holidays.maxHours || 8 : 24}
                onSave={handleSave}
                onDelete={handleDelete}
                onSwipeLeft={goToNextDay}
                onSwipeRight={goToPrevDay}
                onPrevFav={goToPrevFav}
                onNextFav={goToNextFav}
                onSaveNextDay={goToNextDay}
                showSaveNextDay={!isReadOnly && dateIndex < dates.length - 1}
                favIndex={currentFavIndex}
                favCount={favorites.length}
                favorites={favorites}
              />

              {!isReadOnly && (
                <div style={{ padding: '4px 16px 8px' }}>
                  <button
                    className="btn btn-outline btn-block btn-sm"
                    onClick={() => setShowAddJob(true)}
                  >
                    + Add Another Job / Task
                  </button>
                </div>
              )}
            </>
          )}
        </>
      ) : activeTab === 'list' ? (
        <EntryListView
          entries={entries}
          dates={dates}
          isSubmitted={isReadOnly}
          onRefresh={refreshEntries}
          onEntryClick={(jobId, jobTaskId, date) => {
            const favIdx = favorites.findIndex(
              (f) => f.JobID === jobId && f.JobTaskID === jobTaskId
            );
            if (favIdx >= 0) {
              setCurrentFavIndex(favIdx);
              setCurrentDate(date);
              setActiveTab('entry');
            }
          }}
        />
      ) : activeTab === 'tabular' ? (
        <TabularSummary
          entries={entries}
          dates={dates}
          favorites={favorites}
          customers={customers}
          rateCodes={rateCodes}
          defaultRateCodeId={user?.defaultWorkType}
          holidays={holidays}
          isSubmitted={isSubmitted}
          isApproved={isApproved}
          hasOverLimitDay={hasOverLimitDay}
          onSubmit={handleSubmit}
          onReopen={handleReopen}
          onDeleteJob={handleDeleteJob}
          onMoveEntries={handleMoveEntries}
          onReorderJobs={handleReorderJobs}
          onAddFavorite={async (jobId, jobTaskIds) => {
            const ids = Array.isArray(jobTaskIds) ? jobTaskIds : [jobTaskIds];
            for (const tid of ids) {
              const exists = favorites.some((f) => f.JobID === jobId && f.JobTaskID === tid);
              if (!exists) await api.addFavorite(jobId, tid);
            }
            await refreshFavorites();
          }}
          onCellClick={(jobId, jobTaskId, date) => {
            const favIdx = favorites.findIndex(
              (f) => f.JobID === jobId && f.JobTaskID === jobTaskId
            );
            if (favIdx >= 0) {
              setCurrentFavIndex(favIdx);
              setCurrentDate(date);
              setActiveTab('entry');
            }
          }}
          onCellEdit={async ({ jobId, jobTaskId, date, entry, hours, description }) => {
            if (entry) {
              const updated = await api.updateTimeEntry(entry.TimeEntryID, {
                hours, description,
                rateCodeId: entry.RateCodeID,
                nonBillable: entry.NonBillable,
              });
              updateSingleEntry(updated);
            } else if (hours > 0) {
              const fav = favorites.find(f => f.JobID === jobId && f.JobTaskID === jobTaskId);
              const existingEntry = entries.find(e => e.JobID === jobId && e.JobTaskID === jobTaskId);
              const rateCodeId = fav?.DefaultRateCodeID || existingEntry?.RateCodeID || user?.defaultWorkType || '';
              if (!rateCodeId) {
                const favIdx = favorites.findIndex(f => f.JobID === jobId && f.JobTaskID === jobTaskId);
                if (favIdx >= 0) {
                  setCurrentFavIndex(favIdx);
                  setCurrentDate(date);
                  setActiveTab('entry');
                }
                return;
              }
              await api.createTimeEntry({
                timesheetId,
                jobId,
                jobTaskId,
                entryDate: date,
                hours,
                rateSheetCode: fav?.RateSheetCode || '',
                rateCodeId,
                description,
                nonBillable: false,
              });
              await refreshEntries();
            }
          }}
        />
      ) : activeTab === 'byjob' ? (
        <TimesheetSummary
          entries={entries}
          dates={dates}
          isSubmitted={isSubmitted}
          isApproved={isApproved}
          hasOverLimitDay={hasOverLimitDay}
          onSubmit={handleSubmit}
          onReopen={handleReopen}
          onDeleteJob={handleDeleteJob}
          favorites={favorites}
          section="byjob"
          onEntryClick={(jobId, jobTaskId, date) => {
            const favIdx = favorites.findIndex(
              (f) => f.JobID === jobId && f.JobTaskID === jobTaskId
            );
            if (favIdx >= 0) {
              setCurrentFavIndex(favIdx);
              setCurrentDate(date);
              setActiveTab('entry');
            }
          }}
        />
      ) : (
        <TimesheetSummary
          entries={entries}
          dates={dates}
          isSubmitted={isSubmitted}
          isApproved={isApproved}
          hasOverLimitDay={hasOverLimitDay}
          onSubmit={handleSubmit}
          onReopen={handleReopen}
          section="bydate"
          onEntryClick={(jobId, jobTaskId, date) => {
            const favIdx = favorites.findIndex(
              (f) => f.JobID === jobId && f.JobTaskID === jobTaskId
            );
            if (favIdx >= 0) {
              setCurrentFavIndex(favIdx);
              setCurrentDate(date);
              setActiveTab('entry');
            }
          }}
        />
      )}

      {/* Submission Results (errors always visible; details only in debug mode) */}
      {submitResult && (submitResult.error || debugMode) && (
        <div className="card" style={{ position: 'relative' }}>
          <IconButton icon="close" title="Close" onClick={() => setSubmitResult(null)}
            style={{ position: 'absolute', top: '8px', right: '12px' }} />
          <div className="card-header">
            <span className="card-title">
              {submitResult.error ? 'Submission Error' : 'Submission Results'}
            </span>
          </div>
          {submitResult.error && <Banner kind="err" style={{ margin: '0 0 8px' }}>{submitResult.error}</Banner>}
          {debugMode && submitResult._statusDebug && (
            <div style={{
              background: 'var(--gray-50)', padding: '8px', margin: '0 0 8px',
              borderRadius: 'var(--radius)', fontFamily: 'monospace', fontSize: '11px', wordBreak: 'break-all',
            }}>
              <div style={{ fontWeight: 600, marginBottom: '4px' }}>Status Debug</div>
              <div>DB Status: "{submitResult._statusDebug.dbStatus}"</div>
              <div>NAV Status: "{submitResult._statusDebug.navStatus}"</div>
              <div>NAV Category: "{submitResult._statusDebug.category}"</div>
            </div>
          )}
          {debugMode && submitResult.navCall && (
            <div style={{
              background: 'var(--gray-50)', padding: '8px', margin: '0 0 8px',
              borderRadius: 'var(--radius)', fontFamily: 'monospace', fontSize: '11px', wordBreak: 'break-all',
            }}>
              <div style={{ fontWeight: 600, marginBottom: '4px' }}>NAV API Call</div>
              <div>{submitResult.navCall.method || 'POST'} {submitResult.navCall.endpoint}</div>
              {submitResult.navCall.url && <div>URL: {submitResult.navCall.url}</div>}
              {submitResult.navCall.request && <div>Sent: {JSON.stringify(submitResult.navCall.request)}</div>}
              <div>HTTP {submitResult.navCall.status}{submitResult.navCall.durationMs != null && ` (${submitResult.navCall.durationMs}ms)`}</div>
              {submitResult.navCall.response && <div>Response: {JSON.stringify(submitResult.navCall.response)}</div>}
              {submitResult.navCall.error && <div style={{ color: '#dc2626' }}>Error: {submitResult.navCall.error}</div>}
            </div>
          )}
          {debugMode && submitResult.apiResults && (
            <div style={{
              maxHeight: '400px', overflow: 'auto', background: 'var(--gray-50)',
              padding: '8px', borderRadius: 'var(--radius)',
              fontFamily: 'monospace', fontSize: '11px', wordBreak: 'break-all',
            }}>
              {submitResult.apiResults.map((r, i) => (
                <div key={i} style={{
                  padding: '4px 0',
                  borderBottom: i < submitResult.apiResults.length - 1 ? '1px solid var(--gray-200)' : 'none',
                  color: r.ok ? '#059669' : '#dc2626',
                }}>
                  <div style={{ fontWeight: 600 }}>
                    {r.type === 'deleteTimesheet' ? 'DeleteTimesheet' : r.type === 'header' ? 'Header' : 'Line'}
                    {r.type !== 'deleteTimesheet' && ` ${r.lineId}: ${r.jobNo} / ${r.jobTaskNo} / ${r.workType}`}
                    {r.date ? ` (${r.date})` : ''}
                    {r.durationMs != null && ` [${r.durationMs}ms]`}
                  </div>
                  {r.endpoint && <div style={{ color: 'var(--gray-600)' }}>POST {r.endpoint}</div>}
                  <div>Status: {r.ok ? `OK (${r.status})` : `FAIL (${r.status || r.error})`}</div>
                  {r.request && <div style={{ color: 'var(--gray-600)', marginTop: '2px' }}>Sent: {JSON.stringify(r.request)}</div>}
                  {r.data && <div style={{ marginTop: '2px' }}>Response: {JSON.stringify(r.data)}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* NAV Status Debug Info – visible in debug mode */}
      {debugMode && debugInfo?.navHeaderCheck && (
        <div style={{
          margin: '8px 16px', background: 'var(--gray-50)', border: '1px solid var(--gray-200)',
          borderRadius: 'var(--radius)', padding: '8px',
          fontFamily: 'monospace', fontSize: '11px', wordBreak: 'break-all', maxHeight: '400px', overflow: 'auto',
        }}>
          <div style={{ fontWeight: 600, marginBottom: '6px' }}>NAV Status Debug ({debugInfo.totalMs}ms total)</div>
          <div>GET {debugInfo.navHeaderCheck.url || debugInfo.navHeaderCheck.endpoint}</div>
          <div>HTTP {debugInfo.navHeaderCheck.httpStatus} | {debugInfo.navHeaderCheck.durationMs}ms</div>
          <div style={{ marginTop: '4px', borderTop: '1px solid var(--gray-200)', paddingTop: '4px' }}>
            <div>NAV raw status: <strong>"{debugInfo.navHeaderCheck.rawStatus}"</strong> (type: {debugInfo.navHeaderCheck.rawStatusType})</div>
            <div>NAV lowercase: "{debugInfo.navHeaderCheck.navStatus}"</div>
            <div>Category: <strong>{debugInfo.navHeaderCheck.category || 'N/A'}</strong> (matched in: {debugInfo.navHeaderCheck.matchedIn || 'NONE'})</div>
          </div>
          <div style={{ marginTop: '4px', borderTop: '1px solid var(--gray-200)', paddingTop: '4px' }}>
            <div>DB Status Before NAV check: "{debugInfo.navHeaderCheck.dbStatusBefore}"</div>
            <div>Final Status: <strong>"{debugInfo.navHeaderCheck.finalStatus}"</strong></div>
            <div>Status Changed: {String(debugInfo.navHeaderCheck.statusChanged)}</div>
          </div>
          <div style={{ marginTop: '4px', borderTop: '1px solid var(--gray-200)', paddingTop: '4px' }}>
            <div>Current timesheet.Status: <strong>"{timesheet.Status}"</strong></div>
            <div>isApproved={String(isApproved)} | isSubmitted={String(isSubmitted)} | isReadOnly={String(isReadOnly)}</div>
          </div>
          {debugInfo.navHeaderCheck.allFieldNames && (
            <div style={{ marginTop: '4px' }}>NAV record fields: [{debugInfo.navHeaderCheck.allFieldNames.join(', ')}]</div>
          )}
          {debugInfo.navHeaderCheck.statusArrays && (
            <div style={{ marginTop: '4px', borderTop: '1px solid var(--gray-200)', paddingTop: '4px' }}>
              <div>NAV_APPROVED_STATUSES: {JSON.stringify(debugInfo.navHeaderCheck.statusArrays.NAV_APPROVED_STATUSES)}</div>
              <div>NAV_OPEN_STATUSES: {JSON.stringify(debugInfo.navHeaderCheck.statusArrays.NAV_OPEN_STATUSES)}</div>
              <div>NAV_SUBMITTED_STATUSES: {JSON.stringify(debugInfo.navHeaderCheck.statusArrays.NAV_SUBMITTED_STATUSES)}</div>
            </div>
          )}
          {debugInfo.navHeaderCheck.error && <div style={{ color: '#dc2626', marginTop: '4px' }}>Error: {debugInfo.navHeaderCheck.error}</div>}
          {debugInfo.navHeaderCheck.response && (
            <div style={{ marginTop: '4px', borderTop: '1px solid var(--gray-200)', paddingTop: '4px', color: 'var(--gray-600)' }}>
              Full NAV Response: {JSON.stringify(debugInfo.navHeaderCheck.response)}
            </div>
          )}
        </div>
      )}

      {/* Add Job Modal */}
      {showAddJob && (
        <AddJobModal
          customers={customers}
          multiSelect
          onAdd={handleAddFavorite}
          onClose={() => setShowAddJob(false)}
          existingFavorites={favorites}
        />
      )}
    </>
  );
}
